import { AuthContext, type IAuthContext } from '@sangoma/react-oauth2-code-pkce';
import type React from 'react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { LoadingPage } from '../components/LoadingPage/LoadingPage';
import { useHostAuth } from '../hooks/useHostAuth';
import { useMeetingPopupDeviceBridge } from '../hooks/useMeetingPopupDeviceBridge';
import { usePopupHostAuth } from '../hooks/usePopupHostAuth';
import { authStorageService } from '../services/authStorageService';
import { isHostedByTeamHub } from '../services/hostAuthService';
import { isMeetingPopup } from '../services/meetingWindow/popupMode';
import { onLogin, onLogout } from '../telemetry/otelClient';
import type { UserContextType, UserInfo } from '../types/user';
import { decodeJWT } from '../utils/decodeJWT';

const UserContext = createContext<UserContextType | undefined>(undefined);
// Matches the buffer used by @sangoma/react-oauth2-code-pkce's `epochTimeIsPast`
// when deciding whether the access token needs to be refreshed.
const ACCESS_TOKEN_REFRESH_BUFFER_SECONDS = 30;

export interface UserProviderProps {
  children: ReactNode;
}

/**
 * Shared body that takes the raw auth bits (whatever the source) and exposes
 * the `UserContext` shape consumed across NG Meet. Used by both the
 * standalone and hosted providers so callers see one consistent context.
 */
type UserProviderCoreProps = {
  accessToken: string | null;
  idToken: string | null;
  idTokenData: UserInfo | null;
  isLoading: boolean;
  error: string | null;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  getIdToken: () => Promise<string | null>;
  children: ReactNode;
};

const UserProviderCore: React.FC<UserProviderCoreProps> = ({
  accessToken,
  idToken,
  idTokenData,
  isLoading,
  error,
  login,
  logout,
  getAccessToken,
  getIdToken,
  children,
}) => {
  const userInfo: UserInfo | null = idTokenData || null;
  const isAuthenticated = !!idToken && !!userInfo;

  const userName = useMemo(() => {
    if (!userInfo) {
      return 'Guest User';
    }

    return (
      userInfo.name ||
      userInfo.given_name ||
      userInfo.display_name ||
      userInfo.full_name ||
      userInfo.preferred_username?.split('@')[0] ||
      userInfo.email?.split('@')[0] ||
      'User'
    );
  }, [userInfo]);

  const userEmail = useMemo(() => {
    if (!userInfo) {
      return '';
    }

    return userInfo.email || userInfo.preferred_username || userInfo.upn || '';
  }, [userInfo]);

  const userInitials = useMemo(() => {
    if (!userInfo) {
      return 'GU';
    }

    if (userInfo.given_name && userInfo.family_name) {
      return `${userInfo.given_name[0]}${userInfo.family_name[0]}`.toUpperCase();
    }

    if (userInfo.name) {
      const nameParts = userInfo.name.trim().split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
      }
      return nameParts[0].substring(0, 2).toUpperCase();
    }

    if (userName && userName !== 'Guest User') {
      return userName.substring(0, 2).toUpperCase();
    }

    return 'GU';
  }, [userInfo, userName]);

  // `tokenData` (the PKCE-decoded access token) isn't plumbed through the
  // shared core, so derive the expiry directly from the access token. This
  // keeps a single freshness rule for both the standalone (PKCE) and hosted
  // (scuba) sources.
  const isTokenFresh = useMemo(() => {
    if (!accessToken) {
      return false;
    }

    try {
      const expSeconds = Number((decodeJWT(accessToken) as { exp?: number })?.exp);
      if (!Number.isFinite(expSeconds)) {
        return true;
      }

      return Date.now() / 1000 + ACCESS_TOKEN_REFRESH_BUFFER_SECONDS < expSeconds;
    } catch {
      return true;
    }
  }, [accessToken]);

  const contextValue: UserContextType = useMemo(
    () => ({
      userInfo,
      isAuthenticated,

      accessToken: accessToken || null,
      idToken: idToken || null,
      isTokenFresh,
      getAccessToken,
      getIdToken,

      login,
      logout,

      isLoading,
      error,

      userName,
      userEmail,
      userInitials,
    }),
    [
      userInfo,
      isAuthenticated,
      accessToken,
      idToken,
      isTokenFresh,
      getAccessToken,
      getIdToken,
      login,
      logout,
      isLoading,
      error,
      userName,
      userEmail,
      userInitials,
    ],
  );

  const wasAuthenticated = useRef(false);
  useEffect(() => {
    if (isAuthenticated && userInfo) {
      wasAuthenticated.current = true;
      onLogin({
        id: userInfo.sub || userEmail || 'unknown',
        name: userName,
        email: userEmail,
      });
    } else if (!isAuthenticated && wasAuthenticated.current) {
      wasAuthenticated.current = false;
      onLogout();
    }
  }, [isAuthenticated, userInfo, userName, userEmail]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

/**
 * Standalone NG Meet provider. Pulls tokens and user info from the PKCE
 * library's `AuthContext`. Must be wrapped by `<AuthProvider>` upstream
 * (handled in `src/bootstrap.tsx`'s standalone branch).
 */
const StandaloneUserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const {
    token: accessToken,
    idToken,
    idTokenData,
    logIn,
    logOut: rocpLogOut,
    loginInProgress,
    error: authError,
  } = useContext<IAuthContext>(AuthContext);

  const logOut = useCallback(() => {
    authStorageService.clearHasLoggedIn();
    rocpLogOut();
  }, [rocpLogOut]);

  // Cached strings are already kept fresh by the PKCE lib; the async getters
  // simply mirror them so callers can use a single uniform API across modes.
  const accessTokenRef = useRef(accessToken);
  const idTokenRef = useRef(idToken);
  accessTokenRef.current = accessToken;
  idTokenRef.current = idToken;

  const getAccessToken = useCallback(async () => accessTokenRef.current || null, []);
  const getIdToken = useCallback(async () => idTokenRef.current || null, []);

  return (
    <UserProviderCore
      accessToken={accessToken || null}
      idToken={idToken || null}
      idTokenData={idTokenData || null}
      isLoading={loginInProgress}
      error={authError || null}
      login={logIn}
      logout={logOut}
      getAccessToken={getAccessToken}
      getIdToken={getIdToken}
    >
      {children}
    </UserProviderCore>
  );
};

/**
 * Hosted NG Meet provider (MF inside TeamHub). Pulls tokens from the host's
 * `window.appService` (scuba) via `useHostAuth`. No PKCE flow runs.
 */
const HostedUserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const auth = useHostAuth();
  return (
    <UserProviderCore
      accessToken={auth.accessToken}
      idToken={auth.idToken}
      idTokenData={auth.idTokenData}
      isLoading={auth.isLoading}
      error={auth.error}
      login={auth.login}
      logout={auth.logout}
      getAccessToken={auth.getAccessToken}
      getIdToken={auth.getIdToken}
    >
      {children}
    </UserProviderCore>
  );
};

/**
 * Meeting popup provider (hosted-mode launch). The popup is a standalone NG
 * Meet window with no host bridge of its own, so it receives the host identity
 * from its opener over `postMessage` (see `usePopupHostAuth`). We hold the tree
 * behind a loading screen until the handshake delivers the identity so the
 * meeting route never flashes the guest/expired-session views before auth.
 */
const PopupHandoffUserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const auth = usePopupHostAuth();
  const { settingsReady } = useMeetingPopupDeviceBridge();

  if (!auth.idToken) {
    if (auth.error) {
      return <LoadingPage message={'Unable to connect to your meeting session.'} />;
    }
    return <LoadingPage message={'Connecting to your meeting…'} />;
  }

  // The meeting reads prejoin / start-muted straight into the Jitsi iframe
  // config at creation time, so it must not mount before the host's settings
  // land (SM-1188/SM-1190).
  if (!settingsReady) {
    return <LoadingPage message={'Connecting to your meeting…'} />;
  }

  return (
    <UserProviderCore
      accessToken={auth.accessToken}
      idToken={auth.idToken}
      idTokenData={auth.idTokenData}
      isLoading={auth.isLoading}
      error={auth.error}
      login={auth.login}
      logout={auth.logout}
      getAccessToken={auth.getAccessToken}
      getIdToken={auth.getIdToken}
    >
      {children}
    </UserProviderCore>
  );
};

/**
 * UserProvider switches between standalone (PKCE), hosted (scuba), and meeting
 * popup (postMessage handoff) sources. The decision is computed at
 * module-evaluation time so the React tree is stable for the lifetime of the
 * page — there is no "switch mid-session" path.
 */
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  if (isMeetingPopup()) {
    return <PopupHandoffUserProvider>{children}</PopupHandoffUserProvider>;
  }
  if (isHostedByTeamHub()) {
    return <HostedUserProvider>{children}</HostedUserProvider>;
  }
  return <StandaloneUserProvider>{children}</StandaloneUserProvider>;
};

/**
 * Custom hook to access user context
 * Throws an error if used outside of UserProvider
 */
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

/**
 * Custom hook to get just the user info (most common use case)
 */
export const useUserInfo = (): UserInfo | null => {
  const { userInfo } = useUser();
  return userInfo;
};

/**
 * Custom hook to get auth status
 */
export const useAuth = () => {
  const { isAuthenticated, login, logout, isLoading, error } = useUser();
  return { isAuthenticated, login, logout, isLoading, error };
};
